import { useMemo, useState } from "react"
import { AppLayout } from "@/components/layout/app-layout"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import { useAuth } from "@/lib/auth/auth-context"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useTeamMembers } from "@/lib/api/use-teams"
import { useOrganization } from "@/lib/organization/organization-context"
import {
  useSalesLeadsPending,
  useSalesLeadsAssigned,
  useSalesLeadsSearch,
  useAssignSalesManager,
  useChangeLeadDepartment,
} from "@/lib/api/use-sales-leads"
import {
  salesLeadCustomerName,
  salesLeadVehicleName,
  salesLeadVin,
  salesLeadEstimateValue,
  salesLeadUserName,
  type ApiSalesLead,
  type SalesLeadDepartment,
  type TeamMemberSummary,
} from "@/lib/api/types"
import {
  AlertTriangle,
  SearchLg as Search,
  Phone,
  Mail01 as Mail,
  UserPlus01 as UserPlus,
  CheckCircle,
  ClockCheck as Clock,
  User01 as User,
  FilterLines as Filter,
  XClose,
} from "@untitledui/icons"
import { toast } from "sonner"
import { SalesRepView } from "@/components/total-loss/sales-rep-view"
import { SalesManagerView } from "@/components/total-loss/sales-manager-view"
import { motion, AnimatePresence } from "motion/react"
import { useDebounce } from "@/lib/utils/use-debounce"

const DEPARTMENT_LABEL: Record<SalesLeadDepartment, string> = {
  new: "New",
  used: "Used",
}
const DEPARTMENT_BADGE_CLASS: Record<SalesLeadDepartment, string> = {
  new: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  used: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200",
}

type DepartmentFilter = "all" | SalesLeadDepartment

export default function TotalLossPage() {
  const { user } = useAuth()
  const { activeDealershipId } = useOrganization()
  const dealershipTeamId = activeDealershipId ? Number(activeDealershipId) : null

  const currentRole = useCurrentRole()
  // useCurrentRole returns null both for "loading" and "definitely no role".
  // We only know it's the latter after the underlying members query has
  // settled. Until then, withhold view-routing decisions so we don't flash
  // the wrong view at the user (mirrors the no-spinner approach in
  // estimates.tsx — page chrome is up, content fills as data arrives).
  const { isFetched: isMembersFetched } = useTeamMembers(dealershipTeamId)
  const isRoleResolved = !!user?.admin || (dealershipTeamId != null && isMembersFetched)

  const isSuperAdmin = !!user?.admin
  const isDealershipGM = currentRole === Roles.GeneralManager
  const isReadOnlyDealership =
    !isSuperAdmin &&
    !isDealershipGM &&
    (currentRole === Roles.Director ||
      currentRole === Roles.ServiceManager ||
      currentRole === Roles.ServiceAdvisor)
  const isDealershipView = isSuperAdmin || isDealershipGM || isReadOnlyDealership
  const isSalesManager = currentRole === Roles.SalesManager
  const isSalesRep = currentRole === Roles.SalesRepresentative

  // Only swap views once role is definitively known. Default render is the
  // dealership shell — its own empty states cover the brief loading window.
  if (isRoleResolved) {
    if (isSalesRep) return <SalesRepView />
    if (isSalesManager) return <SalesManagerView />
    if (!isDealershipView) {
      return (
        <AppLayout>
          <div className="flex items-center justify-center h-[60vh]">
            <div className="animate-scale-in max-w-md rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-8 text-center">
              <div className="size-14 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="size-6 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2">Access Restricted</h2>
              <p className="text-sm text-zinc-500">
                Total Loss assignments are only available for dealership and sales users.
              </p>
            </div>
          </div>
        </AppLayout>
      )
    }
  }

  return (
    <DealershipTotalLossView
      dealershipTeamId={dealershipTeamId}
      canAssign={isSuperAdmin || isDealershipGM}
      showReadOnlyBanner={isRoleResolved && isReadOnlyDealership}
    />
  )
}

interface DealershipViewProps {
  dealershipTeamId: number | null
  canAssign: boolean
  // Distinct from `!canAssign` so we don't flash the banner during the
  // brief window where the role hasn't resolved yet (canAssign defaults
  // to false in that window for non-admins).
  showReadOnlyBanner: boolean
}

function DealershipTotalLossView({ dealershipTeamId, canAssign, showReadOnlyBanner }: DealershipViewProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 300)
  const [searchScope, setSearchScope] = useState<"visible" | "all">("visible")
  const [selectedTab, setSelectedTab] = useSearchParamState("tab", "pending")
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter>("all")
  const [assignTarget, setAssignTarget] = useState<ApiSalesLead | null>(null)
  const [departmentEditTarget, setDepartmentEditTarget] = useState<number | null>(null)

  const { data: pending = [] } = useSalesLeadsPending(dealershipTeamId)
  const { data: assigned = [] } = useSalesLeadsAssigned(dealershipTeamId)
  // Server-side full search runs only when scope === 'all' AND there's a non-empty term.
  // The hook short-circuits to [] otherwise so React Query doesn't fire wasted requests.
  const { data: serverSearchResults = [] } = useSalesLeadsSearch(
    dealershipTeamId,
    searchScope === "all" ? debouncedSearch : "",
  )
  const { data: teamMembers = [] } = useTeamMembers(dealershipTeamId)
  const salesManagers = useMemo<TeamMemberSummary[]>(
    () => teamMembers.filter(m => m.role === Roles.SalesManager),
    [teamMembers],
  )

  const matchesClientFilter = (lead: ApiSalesLead) => {
    if (departmentFilter !== "all" && lead.department !== departmentFilter) return false
    if (searchScope === "all") return true
    if (debouncedSearch.trim() === "") return true
    const q = debouncedSearch.toLowerCase()
    return (
      salesLeadCustomerName(lead).toLowerCase().includes(q) ||
      salesLeadVehicleName(lead).toLowerCase().includes(q) ||
      (salesLeadVin(lead) ?? "").toLowerCase().includes(q) ||
      (lead.email ?? "").toLowerCase().includes(q) ||
      (lead.phoneNumber ?? "").toLowerCase().includes(q)
    )
  }

  // Server-search mode replaces the visible list entirely with cross-tab results.
  const useServerResults = searchScope === "all" && debouncedSearch.trim() !== ""
  const filteredPending = useServerResults
    ? serverSearchResults.filter(l => !l.salesManager)
    : pending.filter(matchesClientFilter)
  const filteredAssigned = useServerResults
    ? serverSearchResults.filter(l => !!l.salesManager)
    : assigned.filter(matchesClientFilter)

  const tabs = [
    { id: "pending", label: "Pending Assignment", count: pending.length },
    { id: "assigned", label: "Assigned", count: assigned.length },
  ]
  const activeList = selectedTab === "pending" ? filteredPending : filteredAssigned

  return (
    <AppLayout>
      <div className="flex-1 p-4 md:p-8 overflow-auto max-w-[1400px] mx-auto">
        <div className="animate-element mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
            Total Loss Assignments
          </h1>
          <p className="text-sm text-zinc-400 mt-0.5 md:mt-1">
            Convert total loss customers into new vehicle sales
          </p>
          {showReadOnlyBanner && (
            <p className="mt-3 inline-flex items-center gap-2 text-xs text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-2.5 py-1">
              <AlertTriangle className="size-3.5" />
              View-only — assigning leads requires a dealership General Manager
            </p>
          )}
        </div>

        <div className="animate-element animate-delay-100 grid gap-3 md:gap-5 grid-cols-1 md:grid-cols-3 mb-6 md:mb-8">
          <StatCard
            label="Pending Assignment"
            value={pending.length}
            sub="Customers awaiting a sales manager"
            icon={Clock}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
          <StatCard
            label="Assigned"
            value={assigned.length}
            sub="Being handled by sales"
            icon={CheckCircle}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
          />
          <StatCard
            label="Total Leads"
            value={pending.length + assigned.length}
            sub="In the total-loss pipeline"
            icon={User}
            iconBg="bg-primary-50"
            iconColor="text-primary-600"
          />
        </div>

        <div className="animate-element animate-delay-200 rounded-2xl border border-zinc-200/60 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    selectedTab === tab.id
                      ? "bg-zinc-900 text-white shadow-sm"
                      : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-semibold ${
                      selectedTab === tab.id ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-600"
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <div className="relative flex-1 md:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder={searchScope === "all" ? "Search all leads..." : "Filter visible..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full md:w-72 rounded-xl border border-zinc-200 bg-zinc-50 pl-10 pr-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                />
              </div>
              <button
                onClick={() => setSearchScope(s => (s === "visible" ? "all" : "visible"))}
                className={`h-10 px-3 rounded-xl text-xs font-medium transition-colors duration-150 ${
                  searchScope === "all"
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-50 border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                }`}
                title={searchScope === "all" ? "Searching all leads (server)" : "Filtering visible tab (client)"}
              >
                {searchScope === "all" ? "All leads" : "Visible"}
              </button>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value as DepartmentFilter)}
                  className="h-10 appearance-none rounded-xl border border-zinc-200 bg-zinc-50 pl-10 pr-8 text-sm text-zinc-700 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150 cursor-pointer"
                >
                  <option value="all">All departments</option>
                  <option value="new">New</option>
                  <option value="used">Used</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {activeList.length === 0 ? (
              <EmptyState mode={selectedTab as "pending" | "assigned"} />
            ) : (
              <LeadsTable
                leads={activeList}
                showAssignedColumn={selectedTab === "assigned"}
                canAssign={canAssign}
                onAssignClick={setAssignTarget}
                editingDepartmentLeadId={departmentEditTarget}
                onStartEditDepartment={(id) => setDepartmentEditTarget(id)}
                onStopEditDepartment={() => setDepartmentEditTarget(null)}
                dealershipTeamId={dealershipTeamId}
              />
            )}
          </div>
        </div>
      </div>

      <AssignSalesManagerDialog
        open={!!assignTarget}
        lead={assignTarget}
        salesManagers={salesManagers}
        dealershipTeamId={dealershipTeamId}
        onClose={() => setAssignTarget(null)}
      />
    </AppLayout>
  )
}

function StatCard({
  label, value, sub, icon: Icon, iconBg, iconColor,
}: {
  label: string
  value: number | string
  sub: string
  icon: React.ComponentType<{ className?: string }>
  iconBg: string
  iconColor: string
}) {
  return (
    <div className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-4 md:p-6 hover:border-zinc-300/80 transition-all duration-150">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm font-medium text-zinc-500">{label}</p>
        <div className={`size-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className={`size-5 ${iconColor}`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-zinc-900 tracking-tight">{value}</p>
      <p className="text-sm text-zinc-400 mt-1">{sub}</p>
    </div>
  )
}

function EmptyState({ mode }: { mode: "pending" | "assigned" }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
      <div className="size-14 rounded-xl bg-zinc-100 flex items-center justify-center mb-4">
        {mode === "pending" ? (
          <CheckCircle className="size-6 text-zinc-300" />
        ) : (
          <AlertTriangle className="size-6 text-zinc-300" />
        )}
      </div>
      <p className="text-sm font-medium text-zinc-500">
        {mode === "pending" ? "No leads pending assignment" : "No assigned leads"}
      </p>
    </div>
  )
}

interface LeadsTableProps {
  leads: ApiSalesLead[]
  showAssignedColumn: boolean
  canAssign: boolean
  onAssignClick: (lead: ApiSalesLead) => void
  editingDepartmentLeadId: number | null
  onStartEditDepartment: (leadId: number) => void
  onStopEditDepartment: () => void
  dealershipTeamId: number | null
}

function LeadsTable({
  leads, showAssignedColumn, canAssign, onAssignClick,
  editingDepartmentLeadId, onStartEditDepartment, onStopEditDepartment,
  dealershipTeamId,
}: LeadsTableProps) {
  const changeDept = useChangeLeadDepartment(dealershipTeamId)

  const handleDeptChange = (lead: ApiSalesLead, next: SalesLeadDepartment) => {
    if (next === lead.department) {
      onStopEditDepartment()
      return
    }
    changeDept.mutate(
      { leadId: lead.id, data: { department: next } },
      {
        onSuccess: () => {
          toast.success(`Moved to ${DEPARTMENT_LABEL[next]}`)
          onStopEditDepartment()
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to update department")
          onStopEditDepartment()
        },
      },
    )
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-zinc-100">
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Customer</th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Vehicle</th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Dept.</th>
          <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Value</th>
          {showAssignedColumn ? (
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Assigned To</th>
          ) : (
            <th className="px-6 py-3.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Created</th>
          )}
          <th className="px-6 py-3.5 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-50">
        {leads.map((lead) => {
          const customerName = salesLeadCustomerName(lead)
          const initials =
            customerName.split(" ").map(n => n[0]).filter(Boolean).join("") || "?"
          const vehicleName = salesLeadVehicleName(lead)
          const vin = salesLeadVin(lead)
          const value = salesLeadEstimateValue(lead)
          const isEditingDept = editingDepartmentLeadId === lead.id
          return (
            <tr
              key={lead.id}
              className={`animate-fade-in group hover:bg-zinc-50/50 transition-colors duration-100 border-l-[3px] ${
                lead.salesManager ? "border-l-emerald-400" : "border-l-amber-400"
              }`}
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="size-9 rounded-xl bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-600">
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{customerName}</p>
                    <p className="text-xs text-zinc-400">{lead.phoneNumber || "—"}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-medium text-zinc-900">{vehicleName}</p>
                <p className="text-xs text-zinc-400 font-mono">{vin ?? "—"}</p>
              </td>
              <td className="px-6 py-4">
                {isEditingDept && canAssign ? (
                  <select
                    autoFocus
                    defaultValue={lead.department}
                    onBlur={onStopEditDepartment}
                    onChange={(e) => handleDeptChange(lead, e.target.value as SalesLeadDepartment)}
                    disabled={changeDept.isPending}
                    className="h-7 text-xs rounded-md border border-zinc-200 bg-white px-2 outline-none focus:border-zinc-400"
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                  </select>
                ) : (
                  <button
                    type="button"
                    onClick={() => canAssign && onStartEditDepartment(lead.id)}
                    disabled={!canAssign}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${
                      DEPARTMENT_BADGE_CLASS[lead.department]
                    } ${canAssign ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                    title={canAssign ? "Click to change department" : undefined}
                  >
                    {DEPARTMENT_LABEL[lead.department]}
                  </button>
                )}
              </td>
              <td className="px-6 py-4">
                <span className="text-sm font-semibold text-zinc-900 font-mono">
                  {value != null ? `$${value.toLocaleString()}` : "—"}
                </span>
              </td>
              {showAssignedColumn ? (
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary-50 text-primary-700 text-xs font-medium">
                    <User className="size-3" />
                    {salesLeadUserName(lead.salesManager)}
                  </span>
                </td>
              ) : (
                <td className="px-6 py-4 text-sm text-zinc-400">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </td>
              )}
              <td className="px-6 py-4">
                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <button
                    onClick={() => lead.phoneNumber && window.open(`tel:${lead.phoneNumber}`)}
                    disabled={!lead.phoneNumber}
                    className="size-8 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center hover:bg-white hover:border-zinc-300 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Phone className="size-3.5 text-zinc-600" />
                  </button>
                  <button
                    onClick={() => lead.email && window.open(`mailto:${lead.email}`)}
                    disabled={!lead.email}
                    className="size-8 rounded-xl border border-zinc-200 bg-zinc-50 flex items-center justify-center hover:bg-white hover:border-zinc-300 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Mail className="size-3.5 text-zinc-600" />
                  </button>
                  {!lead.salesManager && canAssign && (
                    <button
                      onClick={() => onAssignClick(lead)}
                      className="h-8 px-3 rounded-xl bg-zinc-900 text-white text-xs font-medium flex items-center gap-1.5 hover:bg-black transition-colors duration-100"
                    >
                      <UserPlus className="size-3.5" />
                      Assign
                    </button>
                  )}
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

interface AssignDialogProps {
  open: boolean
  lead: ApiSalesLead | null
  salesManagers: TeamMemberSummary[]
  dealershipTeamId: number | null
  onClose: () => void
}

function AssignSalesManagerDialog({ open, lead, salesManagers, dealershipTeamId, onClose }: AssignDialogProps) {
  const [selectedId, setSelectedId] = useState("")
  const assign = useAssignSalesManager(dealershipTeamId)

  const handleConfirm = () => {
    if (!lead || !selectedId) return
    const managerId = Number(selectedId)
    assign.mutate(
      { leadId: lead.id, data: { salesManagerId: managerId } },
      {
        onSuccess: (updated) => {
          toast.success(`Assigned to ${salesLeadUserName(updated.salesManager)}`, {
            description: `${salesLeadCustomerName(updated)} will be contacted about a replacement vehicle.`,
          })
          setSelectedId("")
          onClose()
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to assign")
        },
      },
    )
  }

  const handleClose = () => {
    setSelectedId("")
    onClose()
  }

  return (
    <AnimatePresence>
      {open && lead && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"
          >
            <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900">Assign to Sales Manager</h3>
                  <p className="text-sm text-zinc-500 mt-1">
                    Assign <strong>{salesLeadCustomerName(lead)}</strong> ({salesLeadVehicleName(lead)}) to a Sales Manager for follow-up.
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 transition-colors duration-100"
                >
                  <XClose className="size-4 text-zinc-500" />
                </button>
              </div>

              <div className="mb-6">
                <label className="text-sm font-medium text-zinc-700 mb-2 block">Sales Manager</label>
                {salesManagers.length === 0 ? (
                  <p className="text-sm text-zinc-400 italic">
                    No Sales Managers on this dealership yet. Invite one from Settings → Team.
                  </p>
                ) : (
                  <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="w-full h-10 appearance-none rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-700 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150 cursor-pointer"
                  >
                    <option value="">Select Sales Manager</option>
                    {salesManagers.map((manager) => (
                      <option key={manager.id} value={String(manager.id)}>
                        {`${manager.firstName} ${manager.lastName}`.trim() || manager.email} — {manager.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 h-10 rounded-xl border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors duration-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!selectedId || assign.isPending}
                  className="flex-1 h-10 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-100"
                >
                  {assign.isPending ? "Assigning…" : "Assign to Sales"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
